/**
 * Image Preview Component
 * Shows thumbnail previews of selected images with remove option
 */
import { useState, useEffect } from 'react';

interface ImagePreviewProps {
  files: File[];
  onRemove: (index: number) => void;
}

export default function ImagePreview({ files, onRemove }: ImagePreviewProps) {
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    // Create object URLs for preview
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviews(urls);

    // Cleanup function to revoke object URLs
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [files]);

  if (files.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 p-2 bg-dark-sidebar rounded-lg">
      {files.map((file, index) => (
        <div
          key={index}
          className="relative group"
        >
          <img
            src={previews[index]}
            alt={file.name}
            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-dark-border"
          />
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="absolute -top-2 -right-2 min-w-[32px] min-h-[32px] sm:w-6 sm:h-6 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 active:bg-red-700 transition-colors flex items-center justify-center"
            title="Remove image"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            {file.name}
          </div>
        </div>
      ))}
    </div>
  );
}
